import type { CatalogueType as ConfigCatalogueType } from '@/config/types'
import { buildImgSrc, buildImgSrcSet } from '@/helpers/image-url'
import { useIntl } from '@/intl'
import { getPublicationCodeByCatalogueAndSiteName } from '@/scopes/common/domain/functions/publicationCodes'
import { PublicationCode, PublicationCodes } from '@/scopes/common/domain/models'
import { SiteName } from '@/scopes/common/domain/types'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { useEnv } from '@/scopes/common/infrastructure/env'
import { publicationCodeSelector } from '@/scopes/search/quick-search/components/selectors'
import { getImageName, getImagePositioning, getSeason } from '@/scopes/search/quick-search/domain/policies/images'
import { useQuickSearchSelector } from '@/scopes/search/quick-search/infrastructure/store/useQuickSearchSelector'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import { mqMin, tok, tokPx } from '@sunwebgroup/ui-components'
import { PropsWithChildren, useMemo } from 'react'
import { css, styled } from 'styled-components'

const HERO_HEIGHTS = { mobile: 360, tablet: 480 } as const
const SRCSET_WIDTHS = [480, 600, 720, 1440, 2000, 2400]

const Wrapper = styled.div<{ $filtersCount: number }>`
  display: flex;
  position: relative;
  width: 100%;
  user-select: none;
  background: ${tok('primitives_color_neutral_0')};

  ${({ $filtersCount }) => {
    const desktop = 48
    const tablet = $filtersCount === 5 ? 180 : 120
    const mobile = $filtersCount === 5 ? 260 : 200

    return css`
      margin-bottom: ${mobile}px;

      @media ${mqMin('primitives_breakpoint_m_min')} {
        margin-bottom: ${tablet}px;
      }

      @media ${mqMin('primitives_breakpoint_l_min')} {
        margin-bottom: ${desktop}px;
      }
    `
  }}
`

const Content = styled.div`
  position: absolute;
  inset: 0;
  user-select: none;
  z-index: 98;
`

const BackgroundImageWrapper = styled.div`
  width: 100%;
  height: ${HERO_HEIGHTS.mobile}px;
  position: relative;

  @media ${mqMin('primitives_breakpoint_m_min')} {
    height: ${HERO_HEIGHTS.tablet}px;
  }
`

const BackgroundImage = styled.img<{ $isVisible: boolean, $positioning?: string }>`
  position: absolute;
  width: 100%;
  height: ${HERO_HEIGHTS.mobile}px;
  object-fit: cover;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  transition: opacity 1s ease-in-out;

  @media ${mqMin('primitives_breakpoint_m_min')} {
    height: ${HERO_HEIGHTS.tablet}px;
  }

  @media (min-width: 460px) {
    object-position: ${({ $positioning }) => $positioning}
  }
`

const HeroTitle = styled(Typography)`
  color: ${tok('primitives_color_neutral_0')};
  text-align: center;
  font-size: ${tokPx('primitives_space_32')};
  text-shadow:
    0 0 2px rgba(0, 0, 0, 0.06),
    0 1px 4px rgba(0, 0, 0, 0.08),
    0 2px 8px rgba(0, 0, 0, 0.1);
  position: absolute;
  top: 40px;
  left: 0;
  right: 0;
  margin: 0 auto;
  max-width: 280px;
  z-index: 98;

  @media ${mqMin('primitives_breakpoint_m_min')} {
    font-size: ${tokPx('primitives_space_40')};
    top: 80px;
    max-width: unset;
  }

  @media ${mqMin('primitives_breakpoint_l_min')} {
    font-size: ${tokPx('primitives_space_48')};
    top: 127px;
  }
`

interface HeroProps extends PropsWithChildren {
  useGenericTitle: boolean
  catalogue: ConfigCatalogueType
  filters: number
  initialPublicationCode: string
}

export function Hero({
  useGenericTitle,
  catalogue,
  filters,
  children,
  initialPublicationCode,
}: HeroProps) {
  const intl = useIntl()
  const mediaServerBaseUrl = useEnv('MEDIA_SERVER_BASE_URL')
  const selectedPublicationCode = useQuickSearchSelector(publicationCodeSelector)

  const publicationCode = (selectedPublicationCode || initialPublicationCode) as PublicationCode
  const isElizaPublication = PublicationCodes.isElizaPublicationCode(publicationCode)
  const isWinterOnlyPublication = PublicationCodes.isWinterOnlyPublicationCode(publicationCode)
  const siteName = useConfiguration('siteName')

  const title = useMemo(() => {
    const messageId = useGenericTitle
      ? 'quicksearch_hero_title'
      : `quicksearch_hero_title_${catalogue}`
    return intl.formatMessage({ id: messageId })
  }, [useGenericTitle, catalogue, intl])

  const buildImageProps = (seasonName: 'summer' | 'winter') => {
    const currentSeason = getSeason(publicationCode)
    const imageName = getImageName(seasonName, siteName as SiteName)
    const imagePublicationCode = getPublicationCodeByCatalogueAndSiteName(isElizaPublication ? 'summer' : isWinterOnlyPublication ? 'winter' : seasonName, siteName as SiteName)
    const positioning = getImagePositioning(imagePublicationCode)

    // If there's no image for this site/season, return a safe, invisible fallback
    if (!imageName) {
      return {
        $isVisible: false,
        src: '',
        srcSet: '',
        sizes: '',
        alt: '',
        fetchpriority: 'high' as const,
        $positioning: positioning,
      }
    }

    const src = buildImgSrc(`${mediaServerBaseUrl}/bg_qs_${imageName}.jpg`, HERO_HEIGHTS.tablet)
    const srcSet = buildImgSrcSet(`${mediaServerBaseUrl}/bg_qs_${imageName}.jpg`, SRCSET_WIDTHS)

    return {
      $isVisible: currentSeason === seasonName,
      src,
      srcSet,
      sizes: '100vw',
      alt: '',
      fetchpriority: 'high' as const,
      $positioning: positioning,
    }
  }

  const summerImageProps = useMemo(() => buildImageProps('summer'), [publicationCode, siteName, mediaServerBaseUrl, isElizaPublication])
  const winterImageProps = useMemo(() => buildImageProps('winter'), [publicationCode, siteName, mediaServerBaseUrl, isElizaPublication])

  return (
    <Wrapper $filtersCount={filters}>
      <HeroTitle forwardedAs="h1" variant="display-b-regular">{title}</HeroTitle>
      <BackgroundImageWrapper>
        <BackgroundImage {...summerImageProps} />
        <BackgroundImage {...winterImageProps} />
      </BackgroundImageWrapper>
      <Content>{children}</Content>
    </Wrapper>
  )
}
